/**
 * Creditos y Movimientos — Capital Humano.
 *
 * Vista global de lo que hasta ahora sólo se podía capturar empleado por empleado desde el Sobre
 * Recibo de una prenómina abierta: créditos INFONAVIT y FONACOT, movimientos e incidencias
 * permanentes, incapacidades y vacaciones.
 *
 * Cada pestaña es una <TablaGlobalNomina /> contra su endpoint `*Empresa` (paginado en el
 * servidor), y la captura se hace en <ModalEmpleadoNomina />, que monta el mismo componente
 * por-empleado del Sobre Recibo. Aquí no se duplica ni un formulario.
 *
 * Las pestañas se montan hasta que se visitan (mismo mecanismo del Sobre Recibo): abrir la
 * pantalla dispara una sola consulta, no seis.
 */

import React, { Component } from 'react'
import axios from 'axios'
import { PageHeader, Tabs, Alert } from 'antd'
import { connect } from 'react-redux'
import { ButtonItem } from '../../../Components/Items'
import { DEFINICIONES } from './definicionesTablas'
import TablaGlobalNomina from './TablaGlobalNomina'
import ModalEmpleadoNomina from './ModalEmpleadoNomina'

const { TabPane } = Tabs

/** Qué catálogo necesita cada pestaña para sus filtros. Se cargan sólo al abrirla. */
const CATALOGOS_POR_CLAVE = {
  movimientos: ['conceptos'],
  incidencias: ['tiposIncidencias'],
  incapacidades: ['tiposIncidencias'],
}

class CreditosMovimientos extends Component {

  state = {
    tabActiva: DEFINICIONES[0].clave,
    visitadas: { [DEFINICIONES[0].clave]: true },
    // Catálogos compartidos entre pestañas; se piden una sola vez.
    conceptos: [],
    tiposIncidencias: [],
    // Modal de captura
    modalVisible: false,
    modalClave: null,
    modalEmpleadoId: null,
    modalTipoPeriodoId: null,
    // Sube en cada guardado del modal para que la tabla de abajo se refresque.
    recargarToken: 0,
  }

  componentDidMount() {
    this.asegurarCatalogos(this.state.tabActiva)
  }

  onCambiarTab = (clave) => {
    this.setState((prev) => ({
      tabActiva: clave,
      visitadas: { ...prev.visitadas, [clave]: true },
    }))
    this.asegurarCatalogos(clave)
  }

  /**
   * Pide los catálogos que le hacen falta a esa pestaña, si no se han pedido antes.
   * El registro de "ya pedido" va en una propiedad de instancia y no en el state a propósito:
   * `setState` es asíncrono, y dos pestañas que comparten catálogo (incidencias e incapacidades)
   * lo pedirían dos veces si el guard dependiera de un state que todavía no se aplicó.
   */
  catalogosPedidos = {}

  asegurarCatalogos(clave) {
    const necesarios = CATALOGOS_POR_CLAVE[clave] || []
    for (const catalogo of necesarios) {
      if (this.catalogosPedidos[catalogo]) continue
      this.catalogosPedidos[catalogo] = true
      if (catalogo === 'conceptos') this.cargarConceptos()
      if (catalogo === 'tiposIncidencias') this.cargarTiposIncidencias()
    }
  }

  async cargarConceptos() {
    const { empresaId, headersToken } = this.props
    try {
      const res = await axios.post(
        'conceptosNomina/selectAllConceptosNomina',
        { empresaId },
        { headers: headersToken }
      )
      if (res.data.success === 2) {
        this.setState({ conceptos: res.data.conceptosNomina || res.data.data || [] })
      }
    } catch { /* el filtro de concepto queda vacío; la tabla funciona igual */ }
  }

  async cargarTiposIncidencias() {
    const { empresaId, headersToken } = this.props
    try {
      const res = await axios.post(
        'tiposIncidencias/selectTodasTiposIncidencias',
        { empresaId },
        { headers: headersToken }
      )
      if (res.data.success === 2) this.setState({ tiposIncidencias: res.data.data || [] })
    } catch { /* idem */ }
  }

  // ── Modal ──────────────────────────────────────────────────────────────────

  abrirModal(clave, registro) {
    this.setState({
      modalVisible: true,
      modalClave: clave,
      modalEmpleadoId: registro?.empleadoId || null,
      modalTipoPeriodoId: registro?.empleadoTipoPeriodoId || null,
    })
  }

  cerrarModal = () => {
    this.setState({ modalVisible: false, modalEmpleadoId: null, modalTipoPeriodoId: null })
  }

  onCambioEnModal = () => {
    this.setState((prev) => ({ recargarToken: prev.recargarToken + 1 }))
  }

  render() {
    const { nomina } = this.props
    const {
      tabActiva, visitadas, conceptos, tiposIncidencias,
      modalVisible, modalClave, modalEmpleadoId, modalTipoPeriodoId, recargarToken,
    } = this.state

    const ctx = { conceptos, tiposIncidencias }
    const definicionModal = DEFINICIONES.find((d) => d.clave === modalClave)

    return (
      <>
        <PageHeader
          title="Creditos y Movimientos"
          subTitle="Todos los empleados de la empresa"
          style={{
            border: '1px solid rgb(235, 237, 240)',
            backgroundColor: 'white',
            marginBottom: 10,
          }}
        />

        {!nomina && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 10 }}
            message="El módulo de nómina no está activo en esta empresa"
            description="Las pestañas de créditos, movimientos e incidencias sólo aplican con nómina activa."
          />
        )}

        <div style={{ background: 'white', padding: '0 12px 12px 12px', borderRadius: 6 }}>
          <Tabs activeKey={tabActiva} onChange={this.onCambiarTab}>
            {DEFINICIONES.map((definicion) => (
              <TabPane tab={definicion.titulo} key={definicion.clave}>
                {visitadas[definicion.clave] && (
                  <>
                    {definicion.enlaceExtra && (
                      <div style={{ marginBottom: 8 }}>
                        <ButtonItem
                          size="small"
                          blanco
                          onClick={() => this.props.history.push(definicion.enlaceExtra.ruta)}
                        >
                          {definicion.enlaceExtra.texto}
                        </ButtonItem>
                      </div>
                    )}
                    <TablaGlobalNomina
                      definicion={definicion}
                      ctx={ctx}
                      recargarToken={recargarToken}
                      onAbrir={(registro) => this.abrirModal(definicion.clave, registro)}
                      onAgregar={() => this.abrirModal(definicion.clave, null)}
                    />
                  </>
                )}
              </TabPane>
            ))}
          </Tabs>
        </div>

        <ModalEmpleadoNomina
          visible={modalVisible}
          clave={modalClave}
          titulo={definicionModal ? definicionModal.titulo : ''}
          empleadoId={modalEmpleadoId}
          tipoPeriodoId={modalTipoPeriodoId}
          // Al cambiar de empleado a mano se pierde su tipo de periodo (el selector no lo trae);
          // el wrapper de incidencias cae entonces al periodo vigente de la empresa.
          onCambiarEmpleado={(valor) =>
            this.setState({ modalEmpleadoId: valor || null, modalTipoPeriodoId: null })}
          onClose={this.cerrarModal}
          onCambio={this.onCambioEnModal}
        />
      </>
    )
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  nomina: state.empresa.nomina,
})

export default connect(mapStateToProps)(CreditosMovimientos)
